import { useMutation, useQueryClient } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";
import { STEP_FREE_ALTERNATIVE_IDS } from "../../utils/accessibilitySelection";

import {
  MattersToYouGroup,
  SubmitAccessibility,
  fetchAccessibilities,
  submitAccessibility,
} from "../../apis";
import type {
  AccessibilityGroup,
  AccessibilityLoadResponse,
  HearAboutUsGroup,
} from "../../types/accessibility";

/**
 * Used only when `/accessibility/load` returns neither
 * `accessibility_features_requirements_hidden_ids` nor
 * `..._show_hidden_by_id`. Every screen that nests "Would any of these also
 * work?" resolves those fields through this hook, so the fallback lives here
 * rather than in each of them: a response missing the fields would otherwise
 * flatten the nested tags on one screen and nest them on the next, depending on
 * which caller happened to carry its own copy of the ids.
 */
const LEGACY_HIDDEN_REQUIREMENT_IDS = STEP_FREE_ALTERNATIVE_IDS;
const LEGACY_SHOW_HIDDEN_BY_ID = 428;

/**
 * The grouped need list, or one nameless group holding the flat `data` when
 * the backend sends no groups (absent or empty) — so the screens render the
 * flat list they render today. Runs inside the query so the fallback is one
 * cached reference, not a new array per render.
 */
export function resolveAccessibilityGroups(
  response: Pick<AccessibilityLoadResponse, "data" | "accessibility_groups">,
): AccessibilityGroup[] {
  if (response.accessibility_groups?.length) {
    return response.accessibility_groups;
  }
  return [
    {
      id: null,
      group_name: "",
      display_order: 0,
      children: response.data ?? [],
    },
  ];
}

/** Stable empty value for the pre-fetch render; never a per-render literal. */
const NO_GROUPS: AccessibilityGroup[] = [];

/**
 * The grouped "How did you hear about us" list, or `[]` when the backend sends
 * none (absent key on an older backend, or empty until every active option is
 * grouped). Unlike the need groups there is no synthetic flat bucket: step 5
 * renders today's flat pills whenever this is empty.
 */
export function resolveHearAboutUsGroups(
  response: Pick<AccessibilityLoadResponse, "how_do_you_hear_about_groups">,
): HearAboutUsGroup[] {
  return response.how_do_you_hear_about_groups ?? [];
}

const NO_HEAR_GROUPS: HearAboutUsGroup[] = [];

const accessibilityQuery = atomWithQuery(() => ({
  queryKey: ["accessibilities"],
  queryFn: async () => {
    const response = await fetchAccessibilities();
    return {
      accessibilityNeedOptions: response.accessibility_need_options as string[],
      filterAccessibility: response.filter_accessibility as any[],
      howDoYouHearAboutOptions: response.how_do_you_hear_about_options as any[],
      // The live API returns `matters_to_you`; `mattersToYou` is accepted too
      // so the camelCase spelling in the API docs doesn't silently yield an
      // empty list if the backend ever switches.
      mattersToYou: (response.matters_to_you ??
        response.mattersToYou ??
        []) as MattersToYouGroup[],
      // The "Would any of these also work?" nesting on the access-needs
      // screen: `showHiddenById` is the tag that reveals `hiddenRequirementIds`
      // (currently 428 "Step-free access" revealing its five near-equivalents).
      // Server-driven so the app doesn't have to hardcode the ids.
      hiddenRequirementIds: (
        response.accessibility_features_requirements_hidden_ids ?? []
      ).map(Number) as number[],
      showHiddenById:
        response.accessibility_features_requirements_show_hidden_by_id == null
          ? null
          : Number(
              response.accessibility_features_requirements_show_hidden_by_id,
            ),
      accessibilityGroups: resolveAccessibilityGroups(response),
      howDoYouHearAboutGroups: resolveHearAboutUsGroups(response),
      data: response,
    };
  },
}));

const submitAccessibilityAtom = atom<SubmitAccessibility | null>(null);

export function useAccessibility() {
  const [{ data, isLoading, isFetching }] = useAtom(accessibilityQuery);
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);
  const queryClient = useQueryClient();
  const {
    mutate,
    data: mutationData,
    error,
    status,
  } = useMutation({
    mutationFn: async (args: SubmitAccessibility) => {
      return submitAccessibility(args);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["userSync"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["savedFilters"],
        exact: false,
      });
    },
  });

  return {
    accessibilityNeedOptions: data?.accessibilityNeedOptions || [],
    filterAccessibility: data?.filterAccessibility || [],
    howDoYouHearAboutOptions: data?.howDoYouHearAboutOptions || [],
    mattersToYou: data?.mattersToYou || [],
    hiddenRequirementIds: data?.hiddenRequirementIds?.length
      ? data.hiddenRequirementIds
      : LEGACY_HIDDEN_REQUIREMENT_IDS,
    showHiddenById: data?.showHiddenById ?? LEGACY_SHOW_HIDDEN_BY_ID,
    accessibilityGroups: data?.accessibilityGroups ?? NO_GROUPS,
    howDoYouHearAboutGroups: data?.howDoYouHearAboutGroups ?? NO_HEAR_GROUPS,
    data: data?.data?.data,
    submitData,
    setSubmitData,
    submitAccessibility: mutate,
    submitAccessibilityResponse: mutationData,
    submitAccessibilityError: error,
    submitAccessibilityStatus: status,
    isAccessibilityLoading: isLoading || isFetching,
  };
}
